import {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {getSelectedFiltersMap} from 'components/Accommodation/OffersFilters/getSelectedFilters'
import {useUrlUpdater} from 'components/data/WithUrlUpdater'
import {usePropertyHighlightsAnalyticsPayload} from 'components/PropertyHighlights'
import {OfferViewType, useOfferViewContext} from 'context/offerViewContext'
import {useHandleScrollToClickedOfferTarget} from 'hooks/useHandleScrollToClickedOfferTarget'
import {useOfferCheckInCheckoutDates} from 'hooks/useOfferCheckInCheckoutDates'
import {useShouldOfferLeadToRooms} from 'hooks/useShouldOfferLeadToRooms'
import {
  BLANK_SPACE_ELEMENTS,
  OfferClickedParams,
  trackLockedOfferClicked,
  trackOfferClicked
} from 'middleware/analytics/actions/trackOfferClicked'
import {
  getHotelContext,
  getPaginationContext
} from 'middleware/analytics/selectors'
import {getAccommodationPageOfferFilters} from 'modules/accommodation/selectors'
import {
  getAnonymousId,
  getCurrencyCode,
  getDeviceType,
  getLocaleCode,
  getLocalTaxesIncluded,
  getTaxesIncluded,
  getUserCountryCode
} from 'modules/meta/selectors'
import {hidePopupOnExit} from 'modules/popupOnExit/slice'
import {
  getAppLockedDealConfig,
  getHotelAddress,
  getHotelCity,
  getHotelImageURIs,
  getHotelName,
  getHotelOfferEntity,
  getHotelStarRating,
  getOfferAccessFlowSettings,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'
import {SearchOffer} from 'modules/sapiSearch/slice'
import {getIsUserSearch} from 'modules/search/selectors'
import {getSearchBoxValues} from 'modules/searchBox/selectors'
import {getTimezone} from 'utils'
import {imageProvider} from 'utils/imageProvider'
import {getOfferPositionFromEntity} from 'utils/offers'
import {SelectedElement} from 'utils/selectedElement'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {SIZES} from '@vio/atlas/Image'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useTrackingTeam} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {customerIo} from '@vio/core/src/analytics/services/CustomerIo'
import {
  Action,
  AnalyticsContext,
  Category,
  ClickType,
  Entity,
  Page,
  SearchOfferContext,
  Team
} from '@vio/core/src/analytics/types/Events'
import {
  getIsPrivateDeal,
  getWhiteLabelUserId,
  selectUserId
} from '@vio/core/src/auth/modules/selectors'
import {
  AuthenticationFlows,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'
import {useOpticks} from '@vio/core/src/experiments/components/OpticksProvider'
import {getHasFreeCancellation} from '@vio/core/src/offer/business/cancellationPenalties'
import getIsBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {getMealsAmenities} from '@vio/core/src/offer/business/meal'
import {
  hasChargeLaterPayment,
  hasPayAtPropertyPayment
} from '@vio/core/src/offer/business/offerPaymentMode'
import {getChargeType, hasOfferTag} from '@vio/core/src/offer/business/offers'
import {OfferTagLabel} from '@vio/core/src/offer/types/offer'
import {getEpochSeconds} from '@vio/core/src/utils/date'
import {noop} from '@vio/core/src/utils/function'
import {useLogIn} from '@vio/shared/src/authentication/hooks/useLogIn'

interface Props {
  hotelId: string
  sourceComponent: string
  offer: SearchOffer
  isSplitBooking?: boolean
  logIn?: ReturnType<typeof useLogIn>
}

interface HandleOfferClickParams {
  offer: SearchOffer
  selectedElementId: string | undefined
  offerContext: SearchOfferContext
  isDiscount?: boolean
  clickType?: ClickType
}

type HandleOfferClick = (params: HandleOfferClickParams) => void

export const useHandleOfferClick = ({
  hotelId,
  sourceComponent,
  offer,
  isSplitBooking,
  logIn = noop
}: Props): HandleOfferClick => {
  const dispatch = useDispatch()
  const anonymousId = useSelector(getAnonymousId)
  const userId = useSelector(selectUserId)
  const whiteLabelUserId = useSelector(getWhiteLabelUserId)
  const currency = useSelector(getCurrencyCode)
  const locale = useSelector(getLocaleCode)
  const isUserSearch = useSelector(getIsUserSearch)
  const localTaxesIncluded = useSelector(getLocalTaxesIncluded)
  const taxesIncluded = useSelector(getTaxesIncluded)
  const {brand} = useBrand()
  const {tracking} = useBrandCapabilities()
  const offerView = useOfferViewContext()
  // Added as part of dcbc5616-property-highlights-v1
  const propertyHighlightsPayload = usePropertyHighlightsAnalyticsPayload(
    offerView === OfferViewType.SRP ? hotelId : undefined
  )
  const {onOfferClick} = useHandleScrollToClickedOfferTarget({offerView})
  const isBofhOffer = getIsBofhOffer(offer)
  const shouldGoToRooms = useShouldOfferLeadToRooms(isBofhOffer)

  const activeOffersFilters = useSelector(getAccommodationPageOfferFilters)

  const {isMobile, isDesktopXs} = useDeviceLayout()
  const deviceType = useSelector(getDeviceType)

  const isMobileOrTablet = isMobile || isDesktopXs

  const {updateLocation} = useUrlUpdater()

  const isPrivate = useSelector(getIsPrivateDeal(offer)) && !isSplitBooking

  // Events will be tracked by the team set in context *except* for locked offers, these are always owned by Retention
  const team = useTrackingTeam()

  const {appDownloadLinkId} = useSelector(state =>
    getAppLockedDealConfig(state, hotelId, offer?.id)
  )

  const {isAppLocked, isAuthLocked} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offer?.id)
  )
  const {offerCheckIn, offerCheckOut} = useOfferCheckInCheckoutDates({
    sourceComponent
  })
  const hotelContext = useSelector(state => getHotelContext(state, hotelId))
  const paginationContext = useSelector(getPaginationContext)
  const address = useSelector(state => getHotelAddress(state, hotelId))
  const hotelName = useSelector(state => getHotelName(state, hotelId))
  const hotelCity = useSelector(state => getHotelCity(state, hotelId))
  const imageURIs = useSelector(state => getHotelImageURIs(state, hotelId))
  const starRating = useSelector(state => getHotelStarRating(state, hotelId))
  const userCountryCode = useSelector(getUserCountryCode)
  const {
    checkIn: searchBoxCheckIn,
    checkOut: searchBoxCheckOut,
    rooms: searchBoxRooms
  } = useSelector(getSearchBoxValues)

  const splitBookingDetails = useSelector(state =>
    getSplitBookingDetails(state, hotelId)
  )

  const hotelOfferEntity = useSelector(state =>
    getHotelOfferEntity(state, hotelId)
  )

  const canPayLater =
    hasPayAtPropertyPayment(offer) ||
    hasChargeLaterPayment(offer, isSplitBooking)

  const Opticks = useOpticks()

  const handleOfferClick = useCallback(
    ({
      offer,
      selectedElementId,
      offerContext: originalOfferContext,
      // This offerPosition is deprecated and should be removed in the future
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isDiscount,
      clickType = ClickType.LeftClick
    }: HandleOfferClickParams): void => {
      // Right/middle/long-press clicks navigate on their own, so they are
      // tracked but must not drive any of the navigation side effects below.
      const isPlainClick = clickType === ClickType.LeftClick
      const cio = customerIo(brand)
      const {calculatedTotalRate, id, yourChoice = null, ...offerProps} = offer
      const hotelImageUrl = imageURIs?.[0]
        ? imageProvider(imageURIs[0], SIZES['large'])
        : undefined

      if (Opticks)
        Opticks.setAudienceSegmentationAttributes({
          isVioClicker: isBofhOffer
        })

      const selectedElement =
        !selectedElementId || BLANK_SPACE_ELEMENTS.has(selectedElementId)
          ? SelectedElement.BlankSpace
          : (selectedElementId as SelectedElement)

      const isCheapest = hasOfferTag(offer, OfferTagLabel.IsCheapest)
      const isTopOffer = hasOfferTag(offer, OfferTagLabel.IsTopOffer)
      const meals = getMealsAmenities(offer)
      const hasFreeCancellation = getHasFreeCancellation(
        offer?.cancellationPenalties
      )

      const offerContext = {
        ...originalOfferContext,
        checkIn: offerCheckIn,
        checkOut: offerCheckOut
      }

      // Correct offer position for the legacy user click event
      const {offerPosition} = getOfferPositionFromEntity(
        offer?.id,
        hotelOfferEntity,
        splitBookingDetails,
        sourceComponent
      )

      // TODO: Search TS preexisting issue
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const trackingParams: OfferClickedParams = {
        ...offerProps,
        offerPosition,
        absoluteHotelPosition: hotelContext?.hotelPosition,
        anchorPrice: offerContext?.anchorPrice,
        highestVisiblePrice: hotelContext?.highestVisiblePrice,
        hotelHasGreatDeal: hotelContext?.hotelTags.includes('price_drop'),
        hotelId,
        hotelPosition: hotelContext?.hotelPosition,
        pageNumber: paginationContext?.pageNumber,
        pageOffset: paginationContext?.pageOffset,
        pageLength: paginationContext?.pageLength,
        calculatedTotalRate: Math.round(calculatedTotalRate * 100) / 100,
        isPrivateDeal: isPrivate,
        offerId: id,
        selectedElement,
        sourceComponent,
        yourChoice,
        // add back V2 mapping
        bookURI: offer.url,
        rateBreakdown: {
          baseRate: offer.rate.base,
          localTaxes: offer.rate.hotelFees,
          taxes: offer.rate.taxes
        },
        canPayLater,
        chargeType: getChargeType(offer),
        isCheapest,
        isTopOffer,
        meals,
        hasFreeCancellation,
        checkIn: offerCheckIn,
        checkOut: offerCheckOut
      }

      if (isAppLocked) {
        dispatch(
          trackEvent({
            category: Category.User,
            entity: Entity.AppExclusiveOffer,
            action: Action.Clicked,
            component: sourceComponent,
            payload: {
              bannerId: appDownloadLinkId,
              selectedElement,
              isDiscount
            },
            analyticsContext: {
              [AnalyticsContext.HotelContext]: hotelContext,
              [AnalyticsContext.OfferContext]: offerContext
            },
            team
          })
        )
        return
      }

      if (isAuthLocked) {
        dispatch(trackLockedOfferClicked(trackingParams))
        dispatch(
          trackEvent({
            category: Category.User,
            entity: Entity.LockedOffer,
            action: Action.Clicked,
            component: sourceComponent,
            analyticsContext: {
              [AnalyticsContext.HotelContext]: hotelContext,
              [AnalyticsContext.OfferContext]: offerContext
            },
            payload: {
              selectedElement,
              isPriceVisible: true,
              isLogoVisible: true,
              isDiscount
            },
            team: Team.Retention
          })
        )

        if (!isPlainClick) return

        if (typeof logIn === 'function') {
          logIn({
            anonymousId,
            flow: AuthenticationFlows.UnlockOverlay,
            sourceComponent: SourceComponentType.UnlockOverlay,
            unlockHotelId: hotelId
          })

          return
        }

        updateLocation({scrollAnchorId: hotelId, anonymousId})
      } else {
        // To not create conflicts with ProviderRedirect events when user clicks on 'View rooms' button on AP
        if (!shouldGoToRooms) {
          if (isPlainClick)
            localStorage.setItem(
              'navigationToAccommodationPage',
              Date.now().toString()
            )
          dispatch(trackOfferClicked(trackingParams))
        }

        dispatch(
          trackEvent({
            category: Category.User,
            entity: Entity.Offer,
            action: Action.Clicked,
            component: sourceComponent,
            payload: {
              selectedElement,
              isDiscount,
              filterApplied: !!activeOffersFilters.length,
              allSelectedFilters: getSelectedFiltersMap(activeOffersFilters),
              // Added as part of dcbc5616-property-highlights-v1
              ...propertyHighlightsPayload,

              clickType
            },
            analyticsContext: {
              [AnalyticsContext.HotelContext]: hotelContext,
              [AnalyticsContext.OfferContext]: offerContext
            },
            team
          })
        )

        if (tracking.hasCioTracking)
          cio.trackEvent({
            name: `${Category.User}_${Entity.Offer}_${Action.Clicked}`,
            pageName: Page.Search,
            userId: userId || whiteLabelUserId,
            anonymousId,
            hotelId,
            starRating,
            hotelName,
            hotelCity,
            hotelAddress: address,
            offerId: id,
            rate: {
              base: offer.rate.base,
              hotelFees: offer.rate.hotelFees,
              taxes: offer.rate.taxes,
              localTaxesIncluded: Boolean(localTaxesIncluded),
              taxesIncluded: Boolean(taxesIncluded),
              totalRate: Math.round(calculatedTotalRate * 100) / 100
            },
            id: userId || whiteLabelUserId,
            checkIn: getEpochSeconds(searchBoxCheckIn),
            checkOut: getEpochSeconds(searchBoxCheckOut),
            pageUrl: window.location.href,
            bookURI: offer.url,
            rooms: searchBoxRooms || '1',
            roomName: offer.roomName,
            currency,
            country: userCountryCode,
            locale,
            isUserSearch,
            providerCode: offerContext?.providerCode,
            proxyProviderCode: offerContext?.proxyProviderCode,
            hotelImageUrl,
            timezone: getTimezone()
          })

        if (!isPlainClick) return

        if (isBofhOffer && shouldGoToRooms) {
          onOfferClick(offer)
          return
        }
        if (isBofhOffer && isMobileOrTablet) {
          if (offerView === OfferViewType.SRP_BEST_DEAL_MODAL) {
            dispatch(hidePopupOnExit())
          }
        }
      }
    },
    [
      anonymousId,
      offerCheckIn,
      offerCheckOut,
      currency,
      dispatch,
      hotelContext,
      hotelId,
      tracking.hasCioTracking,
      isUserSearch,
      locale,
      logIn,
      paginationContext,
      propertyHighlightsPayload,
      sourceComponent,
      team,
      userId,
      Opticks,
      activeOffersFilters,
      appDownloadLinkId,
      brand,
      deviceType,
      hotelOfferEntity,
      isMobileOrTablet,
      isSplitBooking,
      localTaxesIncluded,
      offerView,
      onOfferClick,
      shouldGoToRooms,
      splitBookingDetails,
      taxesIncluded,
      address,
      hotelName,
      imageURIs,
      starRating,
      isBofhOffer
    ]
  )

  return handleOfferClick
}
